import ProtectedMessageHtml from '../ProtectedMessageHtml';
import ProtectedConversationAttachment from '../ProtectedConversationAttachment';
import { protectedFileId } from '../../Services/AuthenticatedFiles';
import { isCapturePageRecording } from '../../Helper/CaptureResult';
import { generateHTML } from "@tiptap/html";
import * as marked from "marked";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { ReactComponent as ChevronRightIcon } from "../../Assets/angle-right-solid.svg";
import { ReactComponent as BugIcon } from "../../Assets/bug-solid.svg";
import { ReactComponent as CaretUpIcon } from "../../Assets/caret-up.svg";
import { ReactComponent as ExclamationTriangleIcon } from "../../Assets/exclamation-triangle.svg";
import { ReactComponent as LinkIcon } from "../../Assets/link-solid.svg";
import { ReactComponent as PaperclipIcon } from "../../Assets/paperclip-solid.svg";
import { ReactComponent as PlusIcon } from "../../Assets/plus-solid.svg";
import { ReactComponent as ReplyIcon } from "../../Assets/reply-solid.svg";
import { ReactComponent as ThumbsDown } from "../../Assets/thumbs-down-regular.svg";
import { ReactComponent as ThumbsDownActive } from "../../Assets/thumbs-down-solid.svg";
import { ReactComponent as ThumbsUp } from "../../Assets/thumbs-up-regular.svg";
import { ReactComponent as ThumbsUpActive } from "../../Assets/thumbs-up-solid.svg";
import { ReactComponent as VideoSlashIcon } from "../../Assets/video-slash.svg";
import { ReactComponent as VideoIcon } from "../../Assets/video.svg";
import { activateOnKey } from "../../Helper/a11y";
import { attachmentDisplayUrl } from "../../Helper/AttachmentDisplayUrl";
import {
  isCaptureLogsNotice,
  parseCaptureCardData,
} from "../../Helper/CaptureRequest";
import { CAPTURE_STRING_DEFAULTS } from "../../Helper/CaptureStrings";
// Loaded before Communicator on purpose: Communicator → Router → Form →
// CommunicationManager is an import cycle that needs this order.
import "../../Helper/CommunicationManager";
import Communicator from "../../Helper/Communicator";
import { contentWithVariables } from "../../Helper/ContentReplace";
import { EffectiveHoursConfig } from "../../Helper/EffectiveHoursConfig";
import { getDataDescription, stripHtml } from "../../Helper/FeedbackItemHelper";
import { handleGleapLink } from "../../Helper/handleGleapLink";
import {
  plainTextToMessageHtml,
  sanitizeBasicFormatting,
  sanitizeMessageHtml,
  sanitizeRichHtml,
} from "../../Helper/sanitize";
import { decodeHtmlEntities } from "../../Helper/HtmlEntities";
import { flattenLegacyArtifacts } from "../../Helper/LegacyArtifacts";
import { getMessageAuthorName } from "../../Helper/MessageAuthorName";
import { isBotAuthored } from "../../Helper/MessageAuthor";
import { getAiChipLabel } from "../../Helper/AiDisclosure";
import { isOnline } from "../../Helper/OnlineState";
import {
  decodeStoredCodeEntities,
  safeMarkdownParse,
} from "../../Helper/SafeMarkdown";
import { replaceAll } from "../../Helper/String";
import { stripEditorBlankParagraphs } from "../../Helper/TipTapContent";
import { useAppViewStore } from "../../Stores/AppView";
import { useConfigStore } from "../../Stores/Config";
import { useFormStore } from "../../Stores/Form";
import { AppMode, useRouterStore } from "../../Stores/Router";
import { useSessionStore } from "../../Stores/Session";
import { Ticket, TicketChatMessage, useTicketStore } from "../../Stores/Ticket";
import ActionButton from "../AIActionButton/AIActionButton";
import { ActivityIcon, PlayCircleIcon } from "../CaptureIcons/CaptureIcons";
import { CaptureRequestCard } from "../CaptureRequestCard/CaptureRequestCard";
import ChatMessageAuthor from "../ChatMessageAuthor/ChatMessageAuthor";
import { ChatWidget } from "../ChatWidget/ChatWidget";
import { Receipt } from "../ChatWidget/ChatWidgetPrimitives";
import {
  chatWidgetFallbackText,
  parseChatWidget,
} from "../../Helper/ChatWidget";
import { defaultAttachmentMessage } from "../ChatMessageComposer/ChatMessageComposer";
import { removeMarkdown } from "../../Helper/RemoveMarkdown";
import FeatureRequestSuggestions from "../FeatureRequestSuggestions/FeatureRequestSuggestions";
import FormLoading from "../FormLoading/FormLoading";
import MessageAudioPlayer from "../MessageAudioPlayer/MessageAudioPlayer";
import {
  MessageVideoPlayer,
  isVideoAttachment,
} from "../MessageVideoPlayer/MessageVideoPlayer";
import QuickReplyButtons from "../QuickReplyButtons/QuickReplyButtons";
import { ratingOptions } from "../RatingFormItem/RatingFormItem";
import ExtensionKit from "../tiptap/extenstions/extension-kit";
import "./ChatMessage.scss";

const renderer = new marked.Renderer();
renderer.link = function ({ href, title, tokens }) {
  const text = tokens.map((token) => token.raw).join("");
  return `<a target="_blank" href="${href}">${text}</a>`;
};

marked.setOptions({
  renderer: renderer,
});

// Attachment thumbnails are `width: 100%` of the bubble, which tops out around
// 400px — ask for 2× so they stay sharp on phone displays. Only HEIC actually
// hits the resizer; see `attachmentDisplayUrl`.
const ATTACHMENT_THUMBNAIL_WIDTH = 800;
const ATTACHMENT_FULLSCREEN_WIDTH = 2048;

// Conversation ratings (BotActionRatingInput) are sent on the 2/4/6/8/10
// scale; replies stored before that switch carry the form scale
// 1/2.5/5/7.5/10. Match either, so the face the customer picked is always the
// highlighted one (10 is the top face on both).
const RATING_REPLY_SCALES = [
  [2, 4, 6, 8, 10],
  [1, 2.5, 5, 7.5, 10],
];

export const ratingReplyIndex = (rating: unknown): number => {
  const value = typeof rating === "string" ? parseFloat(rating) : rating;
  if (typeof value !== "number" || !isFinite(value)) {
    return -1;
  }
  for (const scale of RATING_REPLY_SCALES) {
    const index = scale.indexOf(value);
    if (index !== -1) {
      return index;
    }
  }
  return -1;
};

// Message types that can be picked as a quoted-reply target.
export const quotableMessageTypes = [
  "TEXT",
  "SHARED_COMMENT",
  "USER_TEXT",
  "EMAIL_MESSAGE",
  "BOT",
  "BOT_REPLY",
];

// Message types whose body is prose — the only content that belongs INSIDE the
// grey agent bubble. Cards bring their own surface (video call, feature-request
// suggestions), and bare attachments, source links and the AI footer sit flat
// below the bubble, the way Intercom stacks them.
const bubbledMessageTypes = [
  "TEXT",
  "SHARED_COMMENT",
  "USER_TEXT",
  "EMAIL_MESSAGE",
  "BOT",
  "BOT_REPLY",
];

// Plain-text one-liner of a message, used by the in-bubble quote block and the
// composer's "replying to" strip. Handles markdown strings, TipTap JSON and
// attachment-only messages.
export const getQuotePreviewText = (
  message?: TicketChatMessage | null,
): string => {
  if (!message) {
    return "";
  }

  const contentData = message.data?.content;
  let text = "";

  if (typeof contentData === "string" || contentData instanceof String) {
    text = stripHtml(
      removeMarkdown(flattenLegacyArtifacts(String(contentData))),
    );
  } else if (contentData) {
    try {
      let html = generateHTML(contentData, [...ExtensionKit()]);
      html = replaceAll(html, "><", "> <");
      text = stripHtml(html);
    } catch (exp) {}
  }

  text = (text ?? "").trim();
  if (text.length > 0) {
    return text;
  }

  const firstAttachment = message.attachments?.[0];
  if (firstAttachment) {
    return `📎 ${firstAttachment.name ?? "Attachment"}`;
  }

  return "";
};

function ChatMessage({
  chatMessage,
  firstOfGroup = false,
  lastOfGroup = false,
  hasNextMessage = false,
  isLatestAnswer = false,
  isCreator = false,
  showTime = true,
  typingDelay = 0,
  forceTyping = false,
  imageLoaded = undefined,
  joinVideoCall,
  effectiveHoursConfig,
}: {
  chatMessage: TicketChatMessage;
  /**
   * True for the first message of a consecutive same-author run. Only this one
   * renders the avatar + name header, so a group reads as one person speaking
   * and a change of sender is always visible.
   */
  firstOfGroup?: boolean;
  lastOfGroup?: boolean;
  hasNextMessage?: boolean;
  /**
   * True only for the single most-recent Kai answer of the conversation — the
   * one that carries the "Was this helpful?" rating prompt. Every earlier answer
   * keeps its attribution but shows no thumbs, so the prompt never repeats or
   * jumps around. Computed by ConversationMessageList; standalone renderers
   * (stories) pass it explicitly.
   */
  isLatestAnswer?: boolean;
  isCreator?: boolean;
  showTime?: boolean;
  typingDelay?: number;
  forceTyping?: boolean;
  imageLoaded?: () => void;
  joinVideoCall?: (meetingId: string) => void;
  /**
   * Server-resolved hours config for the conversation's assigned team. When
   * provided, online/reply-time evaluation uses this instead of the global
   * project config. Omit for non-conversation contexts (the widget header, the
   * feedback flow) — they keep using project hours.
   */
  effectiveHoursConfig?: EffectiveHoursConfig;
}) {
  const messageTextRef = useRef<HTMLDivElement>(null);
  const configStore = useConfigStore();
  const currentAction = useTicketStore(
    (store) => store.currentBotAction?.action,
  );
  // Quoted replies send through the regular composer only — while a bot is
  // driving the conversation the composer routes through answerBotAction,
  // which has no reply support.
  const botIsActive = useTicketStore(
    (store) => store.currentTicket?.bot?.active === true,
  );
  // In the chatbar panel the bot-answer footer is reduced to just the "AI" chip:
  // no "answered by AI" label and no thumbs rating (the widget keeps both).
  const isChatbar =
    useRouterStore((store) => store.appMode) === AppMode.CHATBAR;
  const ticketShareToken = useTicketStore(
    (store) => store.currentTicket?.shareToken,
  );
  // A capture request (screenshot / screen recording) asked in this message.
  // A parked workflow step carries the same card data, but it is rendered by
  // BotActionInput (`capture` step) while it is the awaiting action.
  const captureCard =
    chatMessage.data?.captureRequest &&
    chatMessage.data?.action?.type !== "capture"
      ? parseCaptureCardData(chatMessage.data.captureRequest)
      : undefined;
  // One of Kai's interactive cards (show_widget), rendered under the bubble.
  const chatWidget = parseChatWidget(chatMessage.data?.widget);
  // A card this build can't render (a newer type): show its words instead
  // of hiding a card-only message.
  const unknownWidgetText = chatWidget
    ? undefined
    : chatWidgetFallbackText(chatMessage.data?.widget);
  const [isTyping, setIsTyping] = useState(typingDelay > 0 ? true : false);

  // Run a `data-action` clicked in the message text (event delegation): the
  // action name, the ticket's share token and `data-action-data` (JSON) go to
  // the app's registerCustomAction callback.
  const dispatchAction = (actionName: string, rawData?: string | null) => {
    if (!actionName) return;
    let actionData: unknown;
    if (rawData) {
      try {
        actionData = JSON.parse(rawData);
      } catch {
        actionData = undefined;
      }
    }
    Communicator.runCustomAction(
      actionName,
      useTicketStore.getState().currentTicket?.shareToken,
      actionData,
    );
  };

  useEffect(() => {
    const messageElement = messageTextRef.current;
    const handleClick = (e: MouseEvent) => {
      if (e.target instanceof HTMLImageElement) {
        Communicator.openImage(e.target.src);
        return;
      }
      let target = e.target as HTMLElement | null;
      while (
        target &&
        target !== messageElement &&
        !target.hasAttribute?.("data-action")
      ) {
        target = target.parentElement;
      }
      if (
        target &&
        target !== messageElement &&
        target.hasAttribute("data-action")
      ) {
        e.preventDefault();
        dispatchAction(
          target.getAttribute("data-action") ?? "",
          target.getAttribute("data-action-data"),
        );
      }
    };

    messageElement?.addEventListener("click", handleClick);
    return () => messageElement?.removeEventListener("click", handleClick);
  }, []);

  useEffect(() => {
    var timer: any = null;
    if (typingDelay && typingDelay > 0) {
      timer = setTimeout(() => {
        setIsTyping(false);
      }, typingDelay);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  const openingTimeAutoResponderText = () => {
    // When effectiveHoursConfig is provided (per-conversation, server-resolved
    // with the assigned team's hours), evaluate against it. Otherwise fall back
    // to the global project config — preserves header / feedback-flow behaviour.
    const { online, nextOpeningTime, currentOpeningTime } =
      isOnline(effectiveHoursConfig);

    var message = "";
    if (online) {
      // Per-row override wins; then team-level replyTime (already localized
      // by the server); then project-level replyTime key (looked up via
      // translateText). If all are empty, render nothing.
      const text =
        currentOpeningTime?.replyTime ??
        effectiveHoursConfig?.replyTime ??
        configStore.translateText(configStore.config.replyTime);
      message = text && text.trim() !== "" ? `${text}.` : "";
    } else {
      // Team-level out-of-office message (already localized) wins over project.
      const teamOoo =
        effectiveHoursConfig?.staticTranslations?.outOfOfficeMessage;
      if (teamOoo && typeof teamOoo === "string" && teamOoo.trim() !== "") {
        message = teamOoo;
      } else {
        const oooMessage = configStore.translateText("outOfOfficeMessage");
        if (oooMessage.trim() !== "" && oooMessage !== "outOfOfficeMessage") {
          message = configStore.translateText("outOfOfficeMessage");
        } else {
          message = `${configStore.translateText(
            "weAreOffline",
          )}\n\n${configStore.translateText("weAreBack")} <b>🕘 ${configStore.formatRelativeTime(nextOpeningTime)}</b>.`;
        }
      }
    }

    return message;
  };

  const renderContent = () => {
    if (isTyping || forceTyping) {
      return <div className="dot-flashing" />;
    }

    // Pure attachment / voice note: the body is just the "📎 Attachment"
    // placeholder (used when a file or recording is sent with no caption). The
    // attachment itself renders below, so the placeholder text is redundant.
    // Text lives in data.content for user/bot messages (data.text is the older
    // field), so check both.
    const bodyText = chatMessage?.data?.content ?? chatMessage?.data?.text;
    if (
      bodyText === defaultAttachmentMessage &&
      chatMessage.attachments &&
      chatMessage.attachments.length > 0
    ) {
      return null;
    }

    // Capture requests without text: a screenshot / recording sent for one
    // may come without a caption (the attachment is the whole message), and a
    // request may come without a message (the card is) — no empty bubble.
    const hasNoBodyText =
      bodyText === undefined ||
      bodyText === null ||
      (typeof bodyText === "string" && bodyText.trim().length === 0);
    if (hasNoBodyText && unknownWidgetText) {
      return (
        <div className="message-text" style={{ whiteSpace: "pre-line" }}>
          {unknownWidgetText}
        </div>
      );
    }
    if (
      hasNoBodyText &&
      (captureCard ||
        chatWidget ||
        (chatMessage?.data?.capture &&
          chatMessage.attachments &&
          chatMessage.attachments.length > 0))
    ) {
      return null;
    }

    if (chatMessage.type === "FEATURE_REQUEST_DUPLICATES") {
      return <FeatureRequestSuggestions />;
    }

    if (chatMessage?.data?.answerBot && !chatMessage.data?.hasAnswer) {
      return (
        <div className="message-text">
          {configStore.translateText("kaiIssue")}
        </div>
      );
    }

    if (
      chatMessage.type === "TEXT" ||
      chatMessage.type === "USER_TEXT" ||
      chatMessage.type === "SHARED_COMMENT" ||
      chatMessage.type === "EMAIL_MESSAGE" ||
      chatMessage.type === "BOT"
    ) {
      // Special message for reply times.
      if (chatMessage.data?.showReplyTimes) {
        var openingTime = openingTimeAutoResponderText();
        if (!openingTime) {
          return null;
        }

        return (
          <div
            className="message-text"
            dangerouslySetInnerHTML={{
              // Project-configured text (reply time, out-of-office message):
              // basic formatting only, never scripts or handlers.
              __html: sanitizeBasicFormatting(openingTime),
            }}
          ></div>
        );
      }

      // Special message for scheduled events.
      if (chatMessage.data?.scheduledMeetingData) {
        const { scheduledMeetingData } = chatMessage.data;

        return (
          <div className="message-text">
            <h3 className="scheduled-title">
              {configStore.translateText("scheduled")}:{" "}
              {scheduledMeetingData.name}
            </h3>
            <div className="scheduled-date">
              {new Date(scheduledMeetingData?.startTime).toLocaleDateString(
                navigator.language,
                {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                },
              )}
            </div>
            {scheduledMeetingData?.user && (
              <div className="scheduled-user">
                {scheduledMeetingData?.user?.name} (
                <a href={`mailto:${scheduledMeetingData?.user?.email}`}>
                  {scheduledMeetingData?.user?.email}
                </a>
                )
              </div>
            )}
          </div>
        );
      }

      const contentData = chatMessage?.data?.content;
      const userName = useSessionStore.getState().getName();
      const showSources = configStore.config.showSources ?? true;

      // Markdown string content (AI-bot answers): variable substitution →
      // header normalisation → AI source-annotation rewrite → marked →
      // sanitizer. Legacy Styled-reply `<artifact>` blocks become plain
      // Markdown text first, so none of their HTML runs.
      if (
        contentData &&
        (typeof contentData === "string" || contentData instanceof String)
      ) {
        let out = replaceAll(
          flattenLegacyArtifacts(String(contentData)),
          "\n",
          "<br>",
        );
        out = contentWithVariables(out, { name: userName });
        try {
          out = out.replace(/<br\s*\/?>(?=\s*|$)/gi, "\n");
          out = out.replace(/^\s*(#{1,6})/gm, "$1");
          out = out.trim();
          out = out.replace(/\n{2,}/g, "\n\n");
          if (showSources) {
            out = out.replace(
              /\[\^(\d+)\|([^|]+)\|([^\]]+)\]/g,
              `<a href="$3" target="_blank" class="ai-annotation" data-title="$2">$1</a>`,
            );
          } else {
            out = out.replace(/\[\^(\d+)\|([^|]+)\|([^\]]+)\]/g, "");
          }
          out = safeMarkdownParse(out, {
            async: false,
            breaks: true,
            walkTokens: decodeStoredCodeEntities,
          });
        } catch (err) {
          console.error(err);
        }

        return (
          <ProtectedMessageHtml
            ref={messageTextRef}
            className="message-text"
            html={sanitizeMessageHtml(out)}
          />
        );
      }

      // TipTap JSON content.
      let html = "";
      if (contentData) {
        try {
          html = generateHTML(stripEditorBlankParagraphs(contentData), [
            ...ExtensionKit(),
          ]);

          // @ts-ignore - matchAll is fine on string at runtime
          const regex = /<helpcenterarticle.*?>.*?<\/helpcenterarticle>/gs;
          Array.from(html.matchAll(regex)).forEach((match: any) => {
            const original = match[0];
            let replaced = replaceAll(original, "&lt;", "<");
            replaced = replaceAll(replaced, "&gt;", ">");
            replaced = replaceAll(replaced, "&quot;", '"');
            replaced = replaceAll(replaced, "helpcenterarticle", "div");
            html = html.replace(original, replaced);
          });

          html = replaceAll(html, "\n", "<br>");
        } catch (err) {
          console.error(err);
        }
      } else if (
        chatMessage?.data?.text &&
        (typeof chatMessage.data.text === "string" ||
          chatMessage.data.text instanceof String)
      ) {
        html = plainTextToMessageHtml(chatMessage.data.text as string);
      }

      html = contentWithVariables(html, { name: userName });

      return (
        <ProtectedMessageHtml
          ref={messageTextRef}
          className="message-text"
          html={sanitizeRichHtml(html)}
        />
      );
    }
    if (chatMessage.type === "ACTION") {
      const joiningVideoCallId = useRouterStore.getState().joiningVideoCallId;
      const isLoading = chatMessage.data?.meetingId === joiningVideoCallId;

      switch (chatMessage?.data?.type) {
        case "video_call":
          const userName =
            chatMessage.user?.firstName ||
            chatMessage.session?.name ||
            "Someone";
          const isActive = chatMessage.data?.status === "ACTIVE";

          if (!isActive) {
            return (
              <div className="video-call-ended">
                <VideoSlashIcon className="video-call-icon" />
                <span>
                  {useConfigStore.getState().translateText("videoCallEnded")}
                </span>
              </div>
            );
          }

          return (
            <div className="video-call-card">
              <div className="video-call-title">
                <span className="video-call-pulse-dot"></span>
                <span>
                  <strong>{userName}</strong>{" "}
                  {useConfigStore
                    .getState()
                    .translateText("invitedToVideoCall")}
                </span>
              </div>
              <button
                className="video-call-button"
                disabled={isLoading}
                onClick={() => {
                  useRouterStore
                    .getState()
                    .setJoiningVideoCallId(chatMessage?.data?.meetingId);
                  joinVideoCall?.(chatMessage?.data?.meetingId);
                }}
              >
                {isLoading ? (
                  <span className="video-call-spinner" aria-hidden="true" />
                ) : (
                  <VideoIcon className="video-call-icon" />
                )}
                {isLoading
                  ? useConfigStore.getState().translateText("joining")
                  : useConfigStore.getState().translateText("joinCall")}
              </button>
            </div>
          );
        default:
          console.error(`Unknown action type: ${chatMessage?.data?.type}`);
          return <></>;
      }
    }
    if (chatMessage.type === "BOT_REPLY") {
      let message = chatMessage?.data?.content;

      if (Array.isArray(message)) {
        message = message.join(", ");
      } else if (typeof message === "object") {
        message = JSON.stringify(message);
      }

      // The customer's own text is stored HTML-escaped ("a &gt; b"); React
      // escapes it again below, so decode first. Text node only, never HTML.
      if (typeof message === "string") {
        message = decodeHtmlEntities(message);
      }

      return (
        <div className="message-text">
          <p>{message}</p>
        </div>
      );
    }
    if (chatMessage.type === "FEEDBACK_ITEM") {
      const feedbackItem = chatMessage.data as Ticket;

      if (feedbackItem?.title) {
        const formKeys = feedbackItem.form
          ? Object.keys(feedbackItem.form)
          : [];

        if (formKeys.length === 0) {
          return null;
        }

        return (
          <div>
            {feedbackItem.type === "FEATURE_REQUEST" && (
              <strong>{feedbackItem.title}</strong>
            )}
            {formKeys?.length > 0 &&
              formKeys.map((formKey) => {
                const formItem = feedbackItem.form[formKey];

                let value = formItem.value;

                if (formItem.type === "multiplechoice") {
                  value =
                    formItem?.choices?.find(
                      (choice: any) => choice?.id === formItem.value,
                    )?.label ?? "--";
                }

                if (
                  formItem.type === "multiple-choice-multi-select" &&
                  Array.isArray(formItem.value)
                ) {
                  const labels = formItem.value
                    .map((val: string) => {
                      const choice = formItem?.choices?.find(
                        (choice: any) => choice && choice?.id === val,
                      );
                      return choice ? choice.label : null;
                    })
                    .filter(Boolean);

                  value = labels.join(", ") || "--";
                }

                return (
                  <div key={formKey} className="message-form-item">
                    <div className="message-form-item-value">{value}</div>
                  </div>
                );
              })}
          </div>
        );
      }

      const content = getDataDescription({
        data: feedbackItem,
        maxLength: 10000,
      });
      if (content === "No content") {
        return null;
      }

      return (
        <div
          className="message-text"
          style={{
            whiteSpace: "pre-wrap",
          }}
        >
          {content}
        </div>
      );
    }

    return null;
  };

  const renderAdditionalContent = () => {
    if (chatMessage.type === "FEEDBACK_ITEM") {
      const feedbackItem = chatMessage.data as Ticket;

      if (feedbackItem && feedbackItem?.screenRecordingUrl) {
        return (
          <div className="chat-message-image">
            <video muted controls src={feedbackItem?.screenRecordingUrl} />
          </div>
        );
      }

      if (feedbackItem && feedbackItem.screenshotUrl) {
        return (
          <div className="chat-message-image">
            <img
              src={feedbackItem.screenshotUrl}
              alt=""
              role="button"
              tabIndex={0}
              aria-label={configStore.translateTextWithFallback(
                "openScreenshot",
                "Open screenshot",
              )}
              onLoad={() => {
                if (imageLoaded) {
                  imageLoaded();
                }
              }}
              onClick={() => {
                Communicator.openImage(feedbackItem.screenshotUrl || "");
              }}
              onKeyDown={activateOnKey(() => {
                Communicator.openImage(feedbackItem.screenshotUrl || "");
              })}
            />
          </div>
        );
      } else {
        if (feedbackItem.generatingScreenshot) {
          return (
            <div className="chat-message-loading">
              <FormLoading />
            </div>
          );
        }
      }

      return null;
    }

    return null;
  };

  const isFileImage = (file: any) => {
    return file && file["type"] && file["type"].split("/")[0] === "image";
  };

  const isPageRecordingAttachment = (attachment: any) =>
    isCapturePageRecording(chatMessage?.data?.capture, attachment);

  const openAttachment = async (attachment: any) => {
    if (configStore.isApp) {
      Communicator.openURL(attachment.url, false, { placeholders: false });
    } else if (window.self !== window.top) {
      window.open(attachment.url, "_blank");
    } else {
      try {
        const response = await fetch(attachment.url);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", attachment.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (_) {
        window.open(attachment.url, "_blank");
      }
    }
  };

  const renderAttachmentLink = (attachment: any, key: number) => (
    <div
      className="attachment"
      key={key}
      role="button"
      tabIndex={0}
      aria-label={
        attachment.name ||
        configStore.translateTextWithFallback(
          "openAttachment",
          "Open attachment",
        )
      }
      onClick={() => openAttachment(attachment)}
      onKeyDown={activateOnKey(() => openAttachment(attachment))}
    >
      <span className="attachment-link">
        <PaperclipIcon />
        <span>{attachment.name}</span>
      </span>
    </div>
  );

  // "Diagnostic data was shared with the support team." — posted by the
  // server when background logs arrive. A centred system line, not a chat
  // bubble: it may follow a teammate's request, so it must not read as
  // something Kai said (no author, no AI chip, no rating).
  if (isCaptureLogsNotice(chatMessage)) {
    const content = chatMessage.data?.content;
    const noticeText = configStore.translateTextWithFallback(
      "captureLogsShared",
      typeof content === "string" && content.trim().length > 0
        ? content
        : CAPTURE_STRING_DEFAULTS.captureLogsShared,
    );
    return (
      <div
        id={chatMessage.id ? `gleap-message-${chatMessage.id}` : undefined}
        className="chat-message chat-message--system-notice"
        role="note"
      >
        <div className="system-notice-line">
          <ActivityIcon />
          <span>{noticeText}</span>
        </div>
      </div>
    );
  }

  // An answered workflow input (local echo) and a verified / not verified
  // email (stored): a receipt, like an answered chat card.
  const verifyEmailReply =
    chatMessage.type === "BOT_REPLY" ? chatMessage.data?.verifyEmail : undefined;
  if (chatMessage.type === "BOT_INPUT_REPLY" || verifyEmailReply) {
    const isRating = chatMessage?.data?.type === "rateconversation";
    const answer =
      typeof chatMessage?.data?.content === "string"
        ? decodeHtmlEntities(chatMessage.data.content)
        : chatMessage?.data?.content;
    const ratingIndex = ratingReplyIndex(chatMessage?.data?.rating);
    return (
      <div
        id={chatMessage.id ? `gleap-message-${chatMessage.id}` : undefined}
        className={`chat-message chat-message--receipt ${
          lastOfGroup ? "chat-message--last-of-group" : ""
        }`}
      >
        <div className="message-container">
          <div className="cw">
            {verifyEmailReply ? (
              <Receipt
                tone={verifyEmailReply.status === "verified" ? "success" : "neutral"}
                title={verifyEmailReply.label || answer}
                detail={typeof answer === "string" ? answer : undefined}
              />
            ) : (
              <Receipt
                title={chatMessage?.data?.question || answer}
                detail={
                  isRating
                    ? ratingIndex >= 0
                      ? ratingOptions[ratingIndex]
                      : undefined
                    : typeof answer === "string"
                      ? answer
                      : undefined
                }
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  const content = renderContent();
  // Keep rendering when there's no text body but the message carries
  // attachments (e.g. a voice note / file sent with no caption, whose
  // placeholder text renderContent intentionally suppresses).
  const hasAttachments = !!(
    chatMessage.attachments && chatMessage.attachments.length > 0
  );
  if (!content && !hasAttachments && !captureCard && !chatWidget) {
    return null;
  }

  const messageFromKai = !(
    chatMessage?.data?.profileImageUrl ||
    configStore.config.operatorAvatarImage?.length
  );

  const renderCustomActions = () => {
    const allActions = chatMessage.data?.buttonActions ?? [];

    const ctaActions = allActions.filter(
      (a: any) => a?.variant !== "quickReply",
    );
    if (ctaActions.length === 0) {
      return null;
    }

    return (
      <div className="ai-action-buttons">
        {ctaActions.map((action: any, index: number) => (
          <ActionButton key={index} action={action} index={index} />
        ))}
      </div>
    );
  };

  const renderQuickReplyActions = () => {
    const allActions = chatMessage.data?.buttonActions ?? [];
    const quickReplyActions = allActions.filter(
      (a: any) => a?.variant === "quickReply",
    );
    if (quickReplyActions.length === 0) {
      return null;
    }

    return <QuickReplyButtons actions={quickReplyActions} />;
  };

  const customActions = renderCustomActions();

  // The AI attribution footer ("AI · Answered by Kai · <time>") owns the
  // timestamp for bot answers, so the separate `.message-date` line below is
  // suppressed when this shows — avoids the flat layout stacking "Answered by
  // Kai" and "Kai · <time>" as two redundant metadata lines.
  // `agentReply` marks AI-agent replies (Kai Resolve / custom agents) — the
  // chip is the mandatory AI disclosure for those too. Deliberately NOT
  // shipped as `answerBot` server-side: that flag classifies the message as a
  // KB-bot answer and would feed agent output back into KB learning.
  // "Enforce EU AI Act" (organisation setting): the chip also shows in the
  // chatbar and on every other bot-authored row (workflow bot messages, Kai's
  // MCP replies), in a readable size. The reply-times auto responder is a
  // system notice, not an AI answer.
  const euAiActEnforced = configStore.isEuAiActEnforced();
  const isAiAnswer =
    !!chatMessage?.data?.answerBot || !!chatMessage?.data?.agentReply;
  const showAnswerBotFooter = euAiActEnforced
    ? (isAiAnswer || (!isCreator && isBotAuthored(chatMessage))) &&
      !chatMessage?.data?.isThinking &&
      !chatMessage?.data?.showReplyTimes
    : isAiAnswer && !chatMessage?.data?.isThinking && !isChatbar;

  const renderAudioAttachments = () => {
    if (!chatMessage?.data?.audioFile) {
      return null;
    }

    return (
      <div className="ai-audio-attachment">
        <MessageAudioPlayer
          audioSrc={chatMessage.data.audioFile}
          autoPlay={true}
        />
      </div>
    );
  };

  if (
    chatMessage?.data?.collectEmail &&
    (currentAction?.type === "input" || !hasNextMessage)
  ) {
    return null;
  }

  const canReplyTo =
    !!chatMessage.id &&
    !botIsActive &&
    !chatMessage.sending &&
    !chatMessage.error &&
    !isTyping &&
    !forceTyping &&
    !chatMessage.data?.isWritting &&
    !chatMessage.data?.isThinking &&
    quotableMessageTypes.includes(chatMessage.type ?? "");

  // Prose (or a docked reply quote) is what earns a bubble. `content` is null
  // for attachment-only messages, which stay bare so the audio player keeps its
  // `.message-audio:first-child` zero top margin.
  const hasBubbleContent =
    (!!content || !!chatMessage.replyTo) &&
    (isTyping ||
      forceTyping ||
      bubbledMessageTypes.includes(chatMessage.type ?? ""));

  // Only the agent side gets the grey bubble — the customer keeps its brand
  // bubble, which is painted on `.message` itself. The thinking placeholder is
  // bubbled too: it occupies the same slot the answer will, so leaving it flat
  // made the shimmer line sit 18px left of the text that replaces it.
  const showsBubble = !isCreator && hasBubbleContent;

  const scrollToQuotedMessage = () => {
    const quotedId = chatMessage.replyTo?.id;
    if (!quotedId) {
      return;
    }

    const element = document.getElementById(`gleap-message-${quotedId}`);
    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: "smooth", block: "center" });
    element.classList.add("chat-message--flash");
    setTimeout(() => {
      element.classList.remove("chat-message--flash");
    }, 1600);
  };

  const renderReplyQuote = () => {
    if (!chatMessage.replyTo || isTyping || forceTyping) {
      return null;
    }

    const previewText = getQuotePreviewText(chatMessage.replyTo);

    // Media the quote can show as a thumbnail, the way every chat app previews
    // a quoted photo. Only an image qualifies — a file or voice note keeps the
    // text preview ("📎 Attachment"), which already names what it is.
    const thumbnailUrl = chatMessage.replyTo.attachments?.find((attachment) =>
      isFileImage(attachment),
    )?.url;

    return (
      <div
        className="message-reply-quote"
        role="button"
        tabIndex={0}
        aria-label={configStore.translateTextWithFallback(
          "goToQuotedMessage",
          "Go to quoted message",
        )}
        onClick={scrollToQuotedMessage}
        onKeyDown={activateOnKey(scrollToQuotedMessage)}
      >
        <div className="message-reply-quote-body">
          <div className="message-reply-quote-name">
            {getMessageAuthorName(chatMessage.replyTo)}
          </div>
          {previewText && (
            <div className="message-reply-quote-text">{previewText}</div>
          )}
        </div>
        {thumbnailUrl && (
          <img className="message-reply-quote-thumb" src={thumbnailUrl} alt="" />
        )}
      </div>
    );
  };

  return (
    <div
      id={chatMessage.id ? `gleap-message-${chatMessage.id}` : undefined}
      className={`chat-message ${
        chatMessage.type === "FEATURE_REQUEST_DUPLICATES" &&
        "chat-message--featurerequest-duplicates"
      } ${customActions && "chat-message-ai-action"} ${
        forceTyping && "chat-message--typing"
      } ${
        (chatMessage.data?.answerBot || messageFromKai) && "chat-message--aibot"
      } ${isCreator && "chat-message--creator"} ${
        showsBubble && "chat-message--bubbled"
      } ${firstOfGroup && "chat-message--first-of-group"} ${
        lastOfGroup && "chat-message--last-of-group"
      } ${
        chatMessage?.data?.isWritting && "chat-message--writting"
      } ${
        chatMessage?.data?.isThinking && "chat-message--thinking"
      }`}
    >
      <div className="message-container">
        {firstOfGroup && !isCreator && (
          <ChatMessageAuthor
            message={chatMessage}
            live={
              forceTyping ||
              !!chatMessage.data?.isWritting ||
              !!chatMessage.data?.isThinking ||
              isLatestAnswer
            }
            typing={forceTyping || !!chatMessage.data?.isThinking}
            settled={
              !forceTyping &&
              !chatMessage.data?.isWritting &&
              !chatMessage.data?.isThinking
            }
          />
        )}
        <div className="message">
          <>
            {/* Only the prose gets the grey agent bubble. Everything below it —
                sources, action buttons, attachments, the AI footer — stays a
                flat sibling inside `.message`, which is transparent and
                unpadded for agents, so they line up with the bubble's left
                edge for free. */}
            {showsBubble ? (
              <div className="message-bubble">
                {renderReplyQuote()}
                {content}
              </div>
            ) : (
              <>
                {renderReplyQuote()}
                {content}
              </>
            )}
            {captureCard && !isTyping && !forceTyping && (
              <CaptureRequestCard
                card={captureCard}
                ticketShareToken={ticketShareToken}
              />
            )}
            {chatWidget && chatMessage.id && !isTyping && !forceTyping && (
              <ChatWidget commentId={chatMessage.id} widget={chatWidget} />
            )}
            {customActions}
            {/* Quick replies sit with the other options, directly under the
                answer and above the "Was this helpful?" footer. */}
            {renderQuickReplyActions()}
            {!customActions &&
              (() => {
                const sources = chatMessage.data?.sources ?? [];
                const filteredSources = sources
                  .slice(0, 3)
                  .filter((source: any) => {
                    // Always show createfeaturerequest and reportabug types
                    if (
                      source.type === "createfeaturerequest" ||
                      source.type === "reportabug"
                    ) {
                      return true;
                    }

                    if (source.type === "featurerequest") {
                      return true;
                    }

                    // Don't show if sources are disabled
                    if (!(configStore.config.showSources ?? true)) {
                      return false;
                    }

                    // Don't show annotated sources
                    if (source.annotated) {
                      return false;
                    }

                    // Don't show sources without url or title
                    if (
                      !source.url ||
                      source.url.trim() === "" ||
                      !source.title ||
                      source.title.trim() === ""
                    ) {
                      return false;
                    }

                    return true;
                  });

                if (filteredSources.length === 0) {
                  return null;
                }

                return (
                  <div className="link-sources">
                    <div className={`link-sources-links`}>
                      {filteredSources.map((source: any, index: number) => {
                        const onClick = () => {
                          if (source.type === "featurerequest") {
                            useRouterStore
                              .getState()
                              .pushPage("feature-request-details", {
                                id: source.shareToken,
                              });
                          } else if (source.type === "createfeaturerequest") {
                            const flow = "featurerequests";
                            useFormStore.setState({
                              feedbackFlow: flow,
                              isSurveyFlow: false,
                              preFillData: {
                                description: source?.data?.description ?? "",
                                title: source?.data?.title ?? "",
                              },
                            });
                            useRouterStore.getState().pushPage("flow", {
                              flowId: flow,
                              handoffTicketId:
                                useTicketStore.getState().currentTicket?.id,
                            });
                            useAppViewStore.getState().reset();
                          } else if (source.type === "reportabug") {
                            const flow = "bugreporting";
                            useFormStore.setState({
                              feedbackFlow: flow,
                              isSurveyFlow: false,
                              preFillData: {
                                description: source?.data?.description ?? "",
                              },
                            });
                            useRouterStore.getState().pushPage("flow", {
                              flowId: flow,
                              handoffTicketId:
                                useTicketStore.getState().currentTicket?.id,
                            });
                            useAppViewStore.getState().reset();
                          } else {
                            // Check for gleap links.
                            handleGleapLink(source.url, true);
                          }
                        };

                        if (source.type === "createfeaturerequest") {
                          return (
                            <div
                              key={index}
                              className="link-source link-source-button"
                              role="button"
                              tabIndex={0}
                              onClick={onClick}
                              onKeyDown={activateOnKey(onClick)}
                            >
                              <div className="link-source--submiticon">
                                <PlusIcon />
                              </div>
                              <div className="link-source--title">
                                {decodeHtmlEntities(source.title)}
                              </div>
                              <ChevronRightIcon className="link-source--arrow" />
                            </div>
                          );
                        }

                        if (source.type === "reportabug") {
                          return (
                            <div
                              key={index}
                              className="link-source link-source-button"
                              role="button"
                              tabIndex={0}
                              onClick={onClick}
                              onKeyDown={activateOnKey(onClick)}
                            >
                              <div className="link-source--submiticon">
                                <BugIcon />
                              </div>
                              <div className="link-source--title">
                                {decodeHtmlEntities(source.title)}
                              </div>
                              <ChevronRightIcon className="link-source--arrow" />
                            </div>
                          );
                        }

                        return (
                          <div
                            key={index}
                            className={`link-source ${
                              (source.type === "featurerequest" ||
                                source.type === "createfeaturerequest") &&
                              "link-source-button"
                            }`}
                            role="button"
                            tabIndex={0}
                            onClick={onClick}
                            onKeyDown={activateOnKey(onClick)}
                          >
                            {source.type === "featurerequest" && (
                              <div className="link-source--upvotes">
                                <CaretUpIcon />
                                {source.upvotes > 99 ? "99+" : source.upvotes}
                              </div>
                            )}
                            {source.type === "createfeaturerequest" && (
                              <div className="link-source--submiticon">
                                <PlusIcon />
                              </div>
                            )}
                            {source.type !== "featurerequest" &&
                              source.type !== "createfeaturerequest" && (
                                <div className="link-source--submiticon link-source--linkicon">
                                  <LinkIcon />
                                </div>
                              )}
                            <div className="link-source--title">
                              {decodeHtmlEntities(source.title)}
                            </div>
                            <ChevronRightIcon className="link-source--arrow" />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            {chatMessage.attachments &&
              chatMessage.attachments.map((attachment, index) => {
                // A page recording (rrweb JSON) sent for a capture request:
                // nothing to play in the widget — the team replays it in the
                // dashboard. Checked first: it never needs the file itself.
                if (isPageRecordingAttachment(attachment)) {
                  return (
                    <div key={index} className="message-capture-chip">
                      <PlayCircleIcon />
                      <span>
                        {configStore.translateTextWithFallback(
                          "capturePageRecording",
                          CAPTURE_STRING_DEFAULTS.capturePageRecording,
                        )}
                      </span>
                    </div>
                  );
                }
                if (protectedFileId(attachment.url)) return <ProtectedConversationAttachment key={index} attachment={attachment} />;
                if (isFileImage(attachment)) {
                  // The thumbnail and the in-page viewer go through
                  // `attachmentDisplayUrl` so HEIC photos — the default capture
                  // format on most phones, undecodable in the Android WebView
                  // this runs inside — get converted at the CDN edge. Every
                  // other format passes through unchanged.
                  //
                  // `openURL` deliberately does NOT: it hands the url to the
                  // native SDK, which has its own decoders and may save the file
                  // by name. A transformed url still ends in `.heic` while
                  // serving JPEG bytes, and we cannot verify from here what each
                  // host app does with that. `openImage` opens a viewer in the
                  // host PAGE — a browser — so that one does need the transform.
                  const openFullscreen = () => {
                    if (!attachment.url) {
                      return;
                    }
                    if (configStore.isApp) {
                      Communicator.openURL(attachment.url, false, { placeholders: false });
                      return;
                    }
                    Communicator.openImage(
                      attachmentDisplayUrl(
                        attachment,
                        ATTACHMENT_FULLSCREEN_WIDTH,
                      )!,
                    );
                  };

                  return (
                    <img
                      key={index}
                      className="attachment-image"
                      src={attachmentDisplayUrl(
                        attachment,
                        ATTACHMENT_THUMBNAIL_WIDTH,
                      )}
                      alt={attachment.name}
                      role="button"
                      tabIndex={0}
                      aria-label={
                        attachment.name ||
                        configStore.translateTextWithFallback(
                          "openImage",
                          "Open image",
                        )
                      }
                      onLoad={() => {
                        if (imageLoaded) {
                          imageLoaded();
                        }
                      }}
                      onClick={openFullscreen}
                      onKeyDown={activateOnKey(openFullscreen)}
                    />
                  );
                }

                // Check if the attachment is an audio file
                if (attachment?.type?.startsWith("audio/")) {
                  return (
                    <MessageAudioPlayer key={index} audioSrc={attachment.url} />
                  );
                }

                // Videos (screen recordings sent for a capture request, or
                // any mp4 / mov / webm) play inline; formats this browser
                // can't play fall back to the regular attachment link.
                if (isVideoAttachment(attachment)) {
                  return (
                    <MessageVideoPlayer
                      key={index}
                      attachment={attachment}
                      onLoaded={imageLoaded}
                      fallback={renderAttachmentLink(attachment, index)}
                    />
                  );
                }

                return renderAttachmentLink(attachment, index);
              })}
            {renderAudioAttachments()}
          </>
          {/* The attribution + rating footer is shown only on the LAST message
              of a consecutive bot group — repeating it under every grouped
              reply is noise. Non-last grouped answers stay text-only. */}
          {showAnswerBotFooter && lastOfGroup && (
            <>
              <div className="answer-bot">
                {/* The group header above already names the author, so this
                    keeps only the "AI" chip — a transparency signal that the
                    answer was machine-generated, not an attribution. */}
                <div className="answer-bot-attribution">
                  {euAiActEnforced ? (
                    <div className="answer-bot-tag answer-bot-tag--labelled">
                      {getAiChipLabel()}
                    </div>
                  ) : (
                    <div className="answer-bot-tag">AI</div>
                  )}
                  {showTime && !chatMessage.sending && !chatMessage.error && (
                    <span className="answer-bot-time">
                      {moment(chatMessage.createdAt ?? new Date()).fromNow()}
                    </span>
                  )}
                </div>
                {currentAction?.type === "answerbotflow" &&
                  (currentAction?.allowRating ?? true) &&
                  !isChatbar &&
                  !chatMessage?.data?.isWritting &&
                  isLatestAnswer && (
                    <div className="answer-bot-feedback">
                      <span className="answer-bot-feedback-label">
                        {configStore.translateText("kaiRatingQuestion")}
                      </span>
                      {chatMessage.rating === "negative" ? (
                        <ThumbsDownActive className="answer-bot-feedback-action answer-bot-feedback-action--active" />
                      ) : (
                        <ThumbsDown
                          className="answer-bot-feedback-action"
                          role="button"
                          tabIndex={0}
                          aria-label={configStore.translateTextWithFallback(
                            "rateAnswerUnhelpful",
                            "Rate answer as unhelpful",
                          )}
                          onClick={() => {
                            useTicketStore
                              .getState()
                              .rateComment(chatMessage.id!, "negative");
                          }}
                          onKeyDown={activateOnKey(() => {
                            useTicketStore
                              .getState()
                              .rateComment(chatMessage.id!, "negative");
                          })}
                        />
                      )}

                      {chatMessage.rating === "positive" ? (
                        <ThumbsUpActive className="answer-bot-feedback-action answer-bot-feedback-action--active" />
                      ) : (
                        <ThumbsUp
                          className="answer-bot-feedback-action"
                          role="button"
                          tabIndex={0}
                          aria-label={configStore.translateTextWithFallback(
                            "rateAnswerHelpful",
                            "Rate answer as helpful",
                          )}
                          onClick={() => {
                            useTicketStore
                              .getState()
                              .rateComment(chatMessage.id!, "positive");
                          }}
                          onKeyDown={activateOnKey(() => {
                            useTicketStore
                              .getState()
                              .rateComment(chatMessage.id!, "positive");
                          })}
                        />
                      )}
                    </div>
                  )}
              </div>
            </>
          )}
        </div>
        {canReplyTo && (
          <button
            type="button"
            className="message-reply-button"
            aria-label="Reply"
            onClick={() => {
              useTicketStore.getState().setReplyingTo(chatMessage);
            }}
          >
            <ReplyIcon />
          </button>
        )}
        {renderAdditionalContent()}
        {/* The customer knows when they sent their own message, so their
            bubbles carry no timestamp — only the states they DO need to see
            (in flight, failed). Agent groups keep their single time line. */}
        {showTime &&
          !showAnswerBotFooter &&
          (!isCreator || chatMessage.sending || chatMessage.error) && (
          <div className="message-date">
            {chatMessage.sending
              ? `Sending...`
              : chatMessage.error
                ? ""
                : moment(chatMessage.createdAt ?? new Date()).fromNow()}
            {chatMessage.error && (
              <div className="message-date-error">
                <ExclamationTriangleIcon />
                {chatMessage.errorMessage ??
                  "Network error. Please try again later."}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ChatMessage;
